import { useIntl } from 'react-intl';
import { z } from 'zod';

import { isEmptyHtmlContent } from '@/utils/string-util';

/**
 * Hook to create the validation schema for pool configuration
 * including campaign type, dates, from email, and opening messages
 */
export function usePoolSchema() {
  const intl = useIntl();

  const messages = {
    campaignTypeRequired: intl.formatMessage({
      defaultMessage: 'Campaign type is required',
      description: 'Campaign type input required error message',
    }),
    fromEmailRequired: intl.formatMessage({
      defaultMessage: 'Select a “From” email address',
      description: 'From email input required error message',
    }),
    startsAtRequired: intl.formatMessage({
      defaultMessage: 'Start date is required',
      description: 'Start date input required error message',
    }),
    endAtRequired: intl.formatMessage({
      defaultMessage: 'Choose an end date or select “Never expires.”',
      description: 'End date input required error message',
    }),
    audienceIdRequired: intl.formatMessage({
      defaultMessage: 'Select an audience to send this campaign to',
      description: 'Audience ID input required error message',
    }),
    bodyRequired: intl.formatMessage({
      defaultMessage: 'Add a campaign message to continue',
      description: 'Message body input required error message',
    }),
    subjectRequired: intl.formatMessage({
      defaultMessage: 'Subject is required for email campaigns',
      description: 'Subject input required error message for email campaigns',
    }),
  };

  return (
    z
      .object({
        campaignType: z.enum(['email', 'sms']),
        fromEmail: z.string().min(1, {
          message: messages.fromEmailRequired,
        }),
        startsAt: z.string().min(1, {
          message: messages.startsAtRequired,
        }),
        endAt: z.string().nullable(),
        isNeverEnding: z.boolean(),
        openingMessages: z.record(
          z.string(),
          z.object({
            audienceId: z.string().nullable(),
            body: z.string().nullable(),
            subject: z.string().nullable(),
          }),
        ),
      })
      // Validate end date - required if isNeverEnding is false
      .refine(
        data => data.isNeverEnding || (data.endAt && data.endAt.trim() !== ''),
        {
          message: messages.endAtRequired,
          path: ['endAt'],
        },
      )
      // Validate openingMessages fields
      .superRefine((data, ctx) => {
        Object.entries(data.openingMessages).forEach(([openingId, message]) => {
          // Validate audienceId - required for all openings
          if (!message.audienceId || message.audienceId.trim() === '') {
            ctx.addIssue({
              code: 'custom',
              message: messages.audienceIdRequired,
              path: ['openingMessages', openingId, 'audienceId'],
            });
          }

          // Validate body - required for all openings
          if (isEmptyHtmlContent(message.body)) {
            ctx.addIssue({
              code: 'custom',
              message: messages.bodyRequired,
              path: ['openingMessages', openingId, 'body'],
            });
          }

          // Validate subject - required for email campaigns
          if (
            data.campaignType === 'email' &&
            (!message.subject || message.subject.trim() === '')
          ) {
            ctx.addIssue({
              code: 'custom',
              message: messages.subjectRequired,
              path: ['openingMessages', openingId, 'subject'],
            });
          }
        });
      })
  );
}
